import { motion } from 'framer-motion'
import { HeadphonesIcon, UserIcon } from 'lucide-react'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { cn } from '@/lib/utils'

interface ChatMessageProps {
  role: 'user' | 'assistant'
  content: string
  isStreaming?: boolean
}

export function TypingIndicator() {
  return (
    <div className="flex items-end gap-2">
      <Avatar className="h-7 w-7 shrink-0">
        <AvatarFallback className="bg-primary/20 text-primary">
          <HeadphonesIcon className="h-3.5 w-3.5" />
        </AvatarFallback>
      </Avatar>
      <div className="rounded-2xl rounded-bl-sm bg-muted px-4 py-3">
        <div className="flex gap-1 items-center h-4">
          {[0, 1, 2].map((i) => (
            <motion.span
              key={i}
              className="block h-1.5 w-1.5 rounded-full bg-muted-foreground/60"
              animate={{ y: [0, -4, 0] }}
              transition={{
                duration: 0.6,
                repeat: Infinity,
                delay: i * 0.15,
                ease: 'easeInOut',
              }}
            />
          ))}
        </div>
      </div>
    </div>
  )
}

export function ChatMessage({ role, content, isStreaming = false }: ChatMessageProps) {
  const isAssistant = role === 'assistant'

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
      className={cn('flex items-end gap-2', !isAssistant && 'flex-row-reverse')}
    >
      <Avatar className="h-7 w-7 shrink-0">
        <AvatarFallback
          className={cn(
            isAssistant
              ? 'bg-primary/20 text-primary'
              : 'bg-amber-500/20 text-amber-500'
          )}
        >
          {isAssistant ? (
            <HeadphonesIcon className="h-3.5 w-3.5" />
          ) : (
            <UserIcon className="h-3.5 w-3.5" />
          )}
        </AvatarFallback>
      </Avatar>

      <div
        className={cn(
          'max-w-[78%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap',
          isAssistant
            ? 'rounded-bl-sm bg-muted text-foreground'
            : 'rounded-br-sm bg-primary text-primary-foreground'
        )}
      >
        {content}
        {isStreaming && (
          <motion.span
            className="inline-block w-0.5 h-3.5 bg-current ml-0.5 align-middle"
            animate={{ opacity: [1, 1, 0, 0] }}
            transition={{ duration: 0.8, repeat: Infinity, ease: 'linear', times: [0, 0.49, 0.5, 1] }}
          />
        )}
      </div>
    </motion.div>
  )
}
